<template>
  <div
    v-if="node"
    ref="rootEl"
    class="flex h-full w-full flex-col overflow-hidden bg-white"
  >
    <div
      ref="storeDetails"
      class="top-0 flex shrink grow flex-col overflow-y-auto"
    >
      <MediaImage
        v-if="node.image"
        v-bind="node.image"
        img-class="h-[288px] w-full object-cover xxl:h-[initial] xxl:aspect-16/9"
        :image-style="imageStyle"
      />
      <div class="typo-body-1 flex flex-1 flex-col p-16 pb-0 md:px-48 md:pt-40">
        <div
          class="mb-8 text-xs/[24px] uppercase tracking-[1px] text-grey-dark-01"
        >
          {{ overline }}
        </div>
        <h2 class="mb-16 text-2xl/[40px] uppercase">{{ node.title }}</h2>
        <div class="text-grey-dark-01">
          <FieldItemTypeAddress
            v-if="node.address"
            v-bind="node.address"
            class="mb-16 not-italic"
          />
          <ul class="mb-32 flex flex-col gap-12">
            <li v-if="node.phone?.first" class="flex items-center">
              <SpriteSymbol class="mr-8 h-24 w-24" name="phone" />
              <FieldItemTypeTelephone v-bind="node.phone.first" />
            </li>
            <li v-if="mailto" class="flex items-center">
              <SpriteSymbol class="mr-8 h-24 w-24" name="mail" />
              <a :href="mailto" target="_blank">
                {{ node.email }}
              </a>
            </li>
            <li v-if="websiteUrl" class="flex items-center">
              <SpriteSymbol class="mr-8 h-[27px] w-[27px]" name="globe" />
              <a :href="websiteUrl" target="_blank">{{ websiteLabel }}</a>
            </li>
          </ul>
          <ul class="mb-40 flex flex-wrap gap-16">
            <li
              v-for="item in typesList"
              :key="item.type"
              class="flex h-40 w-40 items-center justify-center rounded-full"
              :class="{
                'bg-grey-dark-01 text-white': !isRebranding,
                'bg-pink text-black': isRebranding,
              }"
              :title="item.label"
            >
              <SpriteSymbol
                class="h-24 w-24"
                :name="item.icon"
                :alt="item.label"
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div
      v-show="isMobileOrTablet || ctaUrl"
      ref="buttonContainer"
      class="flex gap-24 px-24 py-16 pb-32 pt-[21px] md:px-48 md:pb-40 md:pt-[26px]"
    >
      <NuxtLink :to="backUrl" class="button w-80 px-0 lg:hidden">
        <SpriteSymbol name="arrow-left" />
      </NuxtLink>
      <DrupalLink v-if="ctaUrl" :to="ctaUrl" class="button w-full">
        {{ ctaLabel }}
        <SpriteSymbol
          v-if="isRebranding"
          class="ml-6 h-24 w-24"
          name="arrow-right"
        />
      </DrupalLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { StorefinderNodeDistributorFragment } from '#build/graphql-operations'

const props = defineProps<{
  id: string
}>()

const { isMobileOrTablet } = useViewport()

const id = computed(() => props.id)

const rootEl = ref<HTMLElement | null>(null)

const { $texts } = useNuxtApp()
const route = useRoute()
const { getDistributorTypes, getDistributorTypeIcon, getDistributorTypeLabel } =
  await useStoreFinder()

const { data: node } = await useAsyncData<
  StorefinderNodeDistributorFragment | undefined
>(
  () => {
    return useCachedGraphqlQuery('distributorRoute', { id: id.value }).then(
      (v) => {
        if (v.data.entityById && 'title' in v.data.entityById) {
          return v.data.entityById
        }
      },
    )
  },
  { watch: [id] },
)

const isRebranding = await useFeatureFlag()

const mailto = computed(() => {
  if (node.value?.email) {
    return 'mailto:' + node.value.email.trim()
  }
})

const websiteUrl = computed(() => node.value?.website?.uri?.path || '')
const websiteLabel = computed(() => websiteUrl.value.replace(/https?:\/\//, ''))

const ctaUrl = computed(() => node.value?.link?.uri?.path)
const ctaLabel = computed(
  () =>
    node.value?.link?.title ||
    $texts('storefinder.moreDetails', 'More details'),
)

const types = computed(() =>
  node.value ? getDistributorTypes(node.value) : [],
)

const typesList = computed(() =>
  types.value.map((type) => {
    return {
      type,
      icon: getDistributorTypeIcon(type),
      label: getDistributorTypeLabel(type),
    }
  }),
)

const overline = computed(() => typesList.value.map((v) => v.label).join(', '))

const backUrl = computed(() => {
  return {
    name: 'storefinder',
    query: route.query,
  }
})

const storeDetails = ref<HTMLDivElement | null>()
const buttonContainer = ref<HTMLDivElement | null>()

let resizeObserver: ResizeObserver | undefined
if (import.meta.client) {
  // ResizeObserver does not exist in SSR context
  resizeObserver = new ResizeObserver(() => {
    if (storeDetails.value && buttonContainer.value) {
      if (storeDetails.value.scrollHeight > storeDetails.value.clientHeight) {
        buttonContainer.value.style.borderTop = '1px solid #efefef'
      } else {
        buttonContainer.value.style.removeProperty('border-top')
      }
    }
  })
}

watch(storeDetails, () => {
  if (storeDetails.value) {
    resizeObserver?.observe(storeDetails.value)
  }
})

const imageStyle = defineImageStyle({
  type: 'sizes',
  sizes: {
    sm: 800,
    md: 470,
    xl: 530,
    xxl: 920,
  },
})
</script>
